<script setup lang="ts">
    import AppCountDown from '@/pages/core/countDown/AppCountDown.vue';
    import { useUserStore } from '@/stores/user';
    import { BetOrderCloseReason, BetOrderStatus, OrderClosingCode } from '@/constants';
    import { useI18n } from 'vue-i18n';
    import { useNumberFormatter } from '@/composables/useNumberFormatter';
    import { orderService } from '@/services';
    import { formatOdds } from '@/helpers';
    import { useAppStore } from '@/stores/app';
    import { failMessage, successMessage } from '@/helpers/message';
    import { OrderItem } from '@/models/order';
    import { usePlatform } from '@/composables/usePlatform';

    const { id = '0' } = defineProps<{
        id: string;
    }>();

    const { t } = useI18n();
    const { formatNumber } = useNumberFormatter();
    const userStore = useUserStore();
    const appStore = useAppStore();
    const orderPlaced = appStore.betFeedData[id].orderPlaced! as OrderItem;
    const platform = usePlatform();

    async function confirm() {
        const response = await orderService.updateStatus(orderPlaced.id, BetOrderStatus.Closing);

        if (response.data.success) {
            orderPlaced.closingCode = response.data.closingCode!;
            switch (response.data.closingCode) {
                case OrderClosingCode.Successful:
                    successMessage(t('order.successUpdated'));
                    break;
                case OrderClosingCode.Failed:
                    failMessage(t('order.failedUpdated'));
                    break;
                case OrderClosingCode.InProgress:
                    failMessage(t('order.failedUpdatedBetInProgress'));
                    break;
            }
        }
    }

    const orderStatusInfo = computed((): string => {
        let orderStatusInfo = '';
        let orderBettingStageInfo = '';
        let color = 'green';

        if (orderPlaced.statusId === BetOrderStatus.Open || orderPlaced.statusId === BetOrderStatus.Closing) {
            if (orderPlaced.activeBettingCount === undefined) {
                color = 'orange';
                orderStatusInfo = t('betDialogBetInfo.orderSubmitted');
                orderBettingStageInfo = '';
            } else if (orderPlaced.betCountWaiting > 0) {
                color = 'orange';
                orderStatusInfo =
                    t('betDialogBetInfo.betsDone', {
                        odds: formatOdds(orderPlaced.betOddsWaiting!),
                        currency: userStore.currency.symbol,
                        stake: orderPlaced.sumBetStakeWaiting,
                    }) + ':';
                orderBettingStageInfo += ' ' + t('betDialogBetInfo.waiting');
            } else if (orderPlaced.activeBettingCount > 0) {
                orderStatusInfo = t('betDialogBetInfo.priceAvailable') + ':';
                orderBettingStageInfo += ' ' + t('betDialogBetInfo.attempting');
            } else {
                color = 'red';
                orderStatusInfo = t('betDialogBetInfo.priceNotAvailable') + ':';
                orderBettingStageInfo += ' ' + t('betDialogBetInfo.lookingForPrice') + ' @' + formatOdds(orderPlaced.odds);
            }

            orderStatusInfo = `<span class="${color}Text">${orderStatusInfo}</span>`;
            orderBettingStageInfo = `<span class="orangeText">${orderBettingStageInfo}</span>`;
        } else if (orderPlaced.statusId === BetOrderStatus.Closed) {
            orderStatusInfo = t('betDialogBetInfo.orderClosed');

            if (orderPlaced.closeReasonId === BetOrderCloseReason.ManuallyClosed) {
                orderStatusInfo += ' (' + t('betDialogBetInfo.manually') + ')';
            } else if (orderPlaced.closeReasonId === BetOrderCloseReason.ScoreChanged) {
                orderStatusInfo += ' (' + t('betDialogBetInfo.scoreChanged') + ')';
            } else if (orderPlaced.closeReasonId === BetOrderCloseReason.TimedOut) {
                orderStatusInfo += ' (' + t('betDialogBetInfo.timedOut') + ')';
            }

            if (orderPlaced.betCountWaiting > 0) {
                color = 'orange';
                orderStatusInfo = t('betDialogBetInfo.betsDone', {
                    odds: formatOdds(orderPlaced.betOddsWaiting!),
                    currency: userStore.currency.symbol,
                    stake: orderPlaced.sumBetStakeWaiting,
                });
                orderBettingStageInfo += ' ' + t('betDialogBetInfo.waiting');
            } else if (orderPlaced.betCount && orderPlaced.betCount > 0) {
                orderBettingStageInfo +=
                    ' ' +
                    t('order.betDone', {
                        betCount: orderPlaced.betCount,
                        count: orderPlaced.betCount,
                    }) +
                    ' @' +
                    orderPlaced.betOddsAvg;
            } else {
                color = 'red';
                orderBettingStageInfo += ' ' + (orderPlaced.closeReasonId === BetOrderCloseReason.ManuallyClosed ? t('betDialogBetInfo.noBets') : t('betDialogBetInfo.noBetsDone'));
            }

            orderStatusInfo = `<span class="${color}Text mr--4px">${orderStatusInfo}:</span>`;
            orderBettingStageInfo = `<span class="${color}Text">${orderBettingStageInfo}</span>`;
        }

        return orderStatusInfo + orderBettingStageInfo;
    });

    const progressColor = computed((): string => {
        let color = 'green';

        if (orderPlaced.betCount === undefined || ((orderPlaced.statusId === BetOrderStatus.Open || orderPlaced.statusId === BetOrderStatus.Closing) && orderPlaced.betCountWaiting > 0)) {
            color = 'orange';
        } else if (orderPlaced.statusId === BetOrderStatus.Closed && orderPlaced.betCount === 0) {
            color = 'red';
        }

        return color;
    });

    const progressIndeterminate = computed((): boolean => {
        return orderPlaced.statusId === BetOrderStatus.Open;
    });
</script>
<template>
    <v-container class="pa-0 ma-0" fluid>
        <v-card class="h-bet-info" :class="platform.isBookmaker() ? 'mt-4' : ''" elevation="2" style="margin-left: 2px; margin-right: 2px">
            <v-row class="px-3 pt-4">
                <v-col class="font14 pl-2 pr-0 py-1 d-flex text-center flex-column align-start justify-center">
                    <span class="lightGreyText">
                        {{ t('betDialogConfirm.orderPlaced') }}
                        <v-chip
                            v-if="orderPlaced.statusId === BetOrderStatus.Open || orderPlaced.statusId === BetOrderStatus.Closing || orderPlaced.betCountWaiting > 0"
                            color="orange"
                            label
                            variant="flat"
                            class="mb-1 chip-status"
                        >
                            <span class="text-white bold">{{ t('betDialogConfirm.orderStatusOpen') }}</span>
                        </v-chip>
                        <v-chip v-else-if="orderPlaced.betCount > 0" color="tbGreen" label variant="flat" class="mb-1 chip-status">
                            <span class="text-white bold">{{ t('betDialogConfirm.orderStatusClosed') }}</span>
                        </v-chip>
                        <v-chip v-else color="red" label variant="flat" class="mb-1 chip-status">
                            <span class="text-white bold">{{ t('betDialogConfirm.orderStatusClosed') }}</span>
                        </v-chip>
                    </span>
                    <span class="">#{{ orderPlaced.id }}</span>
                </v-col>
                <v-col class="font14 px-0 py-1 d-flex text-center flex-column align-start justify-center">
                    <span class="lightGreyText">
                        {{ t('betDialogConfirm.stakeRequested') }}
                    </span>
                    <span class="">{{ userStore.currency.symbol }}{{ formatNumber(orderPlaced.stake, 'money') }}</span>
                </v-col>
                <v-col class="font14 px-0 py-1 d-flex text-center flex-column align-start justify-center">
                    <span class="lightGreyText">
                        {{ t('betDialogConfirm.stakeDone') }}
                    </span>
                    <span v-if="orderPlaced.betCount !== undefined" :class="orderPlaced.betCountWaiting > 0 ? 'orangeText bold' : Number(orderPlaced.sumBetStake) > 0 ? 'greenText bold' : 'redText'">
                        {{ userStore.currency.symbol }}{{ formatNumber(orderPlaced.sumBetStake, 'money') }} ({{ orderPlaced.fillPerc }}%)
                    </span>
                    <span v-else>&nbsp;</span>
                </v-col>
            </v-row>

            <v-row class="px-3">
                <v-col class="py-5 font14 text-center bold px-1" v-html="orderStatusInfo"></v-col>
                <v-progress-linear :color="progressColor" :indeterminate="progressIndeterminate" class="mx-2"></v-progress-linear>
            </v-row>
            <v-row class="px-3 h-70">
                <v-col cols="3"></v-col>
                <v-col class="text-center d-flex flex-column justify-center align-center">
                    <v-btn
                        tag="a"
                        density="default"
                        v-if="orderPlaced.statusId === BetOrderStatus.Open && (orderPlaced.timeout === null || (orderPlaced.leftSeconds && orderPlaced.timeout !== 0))"
                        :disabled="!orderPlaced.isCancelAvailable"
                        :color="orderPlaced.isCancelAvailable ? 'cancelOrderBtn' : 'cancelOrderBtnDisabled'"
                        @click="confirm()"
                    >
                        <span class="cancel-order" :class="orderPlaced.isCancelAvailable ? '' : 'cancelOrderBtnDisabled'">{{ t('action.cancelOrder') }}</span>
                    </v-btn>
                    <span
                        v-else-if="orderPlaced.closingCode === OrderClosingCode.InProgress && orderPlaced.timeout !== 0"
                        class="redText pt-12"
                        style="position: absolute; left: 0; right: 0; text-align: center; white-space: nowrap"
                    >
                        {{ t('betDialogConfirm.betProcessing') }}
                    </span>
                </v-col>
                <v-col cols="3" class="px-0 py-1 px-1 d-flex text-center flex-column align-start justify-center pl-2">
                    <div
                        v-if="orderPlaced.statusId === BetOrderStatus.Open && (orderPlaced.timeout === null || (orderPlaced.leftSeconds && orderPlaced.timeout !== 0))"
                        class="timeout d-flex flex-column justify-center align-center"
                    >
                        <span v-if="orderPlaced.statusId === BetOrderStatus.Open && Number(orderPlaced.leftSeconds) > 0" class="orangeText bold">
                            <AppCountDown :fromTime="orderPlaced.createdAt" :timeout="orderPlaced.timeout" :model-value="orderPlaced.leftSeconds!"></AppCountDown>
                        </span>
                        <span v-else-if="orderPlaced.statusId === BetOrderStatus.Open && orderPlaced.timeout === null" class="orangeText">
                            <span>{{ t('panel.kickOff') }}</span>
                        </span>
                        <span v-else>&nbsp;</span>
                    </div>
                </v-col>
            </v-row>
        </v-card>
    </v-container>
</template>

<style scoped>
    .timeout {
        border: solid 2px orange;
        border-radius: 4px;
        width: 85px;
        height: 36px;
    }
    .h-bet-info {
        height: 189px;
    }
    .chip-status {
        max-height: 18px;
    }
    :deep(.v-btn--disabled.v-btn--variant-elevated .v-btn__overlay) {
        opacity: 0;
    }
</style>
